import type { ReactNode } from 'react';

export interface HeroCardProps {
  avatar?: ReactNode;
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  /** Make the avatar tappable (Profile uses this to open avatar picker). */
  onAvatarClick?: () => void;
}

/**
 * Glass hero card with avatar / title / subtitle / trailing slots. A
 * translucent surface (the shared --glass-* vars from ProfilePage) so the
 * ambient Profile glow reads through it; the theme --evi-text foreground keeps
 * it legible on dark and light. ProfilePage is the only consumer.
 */
export function HeroCard({
  avatar,
  eyebrow,
  title,
  subtitle,
  trailing,
  onAvatarClick,
}: HeroCardProps) {
  // Glassmorphism pass: the identity card is a translucent glass surface so
  // the ambient Profile glow reads through it. ProfilePage is the only
  // HeroCard consumer, so this is scoped to the profile greeting. Colours
  // come from the shared --glass-* vars defined on ProfilePage's .page
  // (inherited here) + the theme --evi-text token, so it stays dark/light
  // safe and falls back to the solid surface where backdrop-filter is
  // unsupported. `glow` no longer drives a continuous breathe — the calmer,
  // technological read comes from the material + the glow beneath it.
  const bg = 'var(--glass-specular), var(--glass-fill)';
  const fg = 'var(--evi-text)';

  return (
    <div
      style={{
        background: bg,
        backdropFilter: 'var(--glass-blur)',
        WebkitBackdropFilter: 'var(--glass-blur)',
        borderRadius: 20,
        padding: 18,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        color: fg,
        border: '1px solid var(--glass-edge)',
        boxShadow: 'var(--glass-lift)',
      }}
    >
      {avatar && (
        <button
          type="button"
          onClick={onAvatarClick}
          disabled={!onAvatarClick}
          aria-label={onAvatarClick ? 'Change avatar' : undefined}
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'var(--glass-well)',
            overflow: 'hidden',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            padding: 0,
            cursor: onAvatarClick ? 'pointer' : 'default',
            position: 'relative',
          }}
        >
          {avatar}
        </button>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        {eyebrow && (
          <div
            style={{
              fontSize: 10,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              color: 'var(--evi-text-mute)',
              fontWeight: 500,
              marginBottom: 3,
            }}
          >
            {eyebrow}
          </div>
        )}
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: 0.2,
            lineHeight: 1.15,
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--evi-text-mute)',
              marginTop: 3,
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      {trailing && <div style={{ flexShrink: 0 }}>{trailing}</div>}
    </div>
  );
}
